/**
 * Puente de tokens del tablero de indicadores.
 *
 * <b>Por qué existe.</b> El tablero pinta con dos tecnologías que no se hablan:
 *
 *  - Los contenedores usan clases de Tailwind, que resuelven variables CSS solas.
 *  - Los gráficos usan `recharts`, que recibe los colores como **prop string**
 *    (`fill`, `stroke`) y no entiende una clase de utilidad.
 *
 * Sin un puente, cada widget de gráfico terminaría con hexadecimales cableados —
 * que es exactamente lo que hay hoy en `chartColors.ts`— y el modo oscuro no
 * podría cambiarlos. El kit ya resolvió este mismo problema una vez con el puente
 * `--mpv-*` de MasterPatternView, y esto sigue ese precedente.
 *
 * <b>La regla del módulo:</b> ningún componente del tablero declara un hexadecimal.
 * Los colores salen de aquí o de `CATEGORICAL_PALETTE`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

/** Los tokens que el tablero consume. Cada uno tiene su `--dash-*` en `index.css`. */
export const DASHBOARD_TOKEN_NAMES = [
  'accent',
  'success',
  'danger',
  'text-primary',
  'text-secondary',
  'text-muted',
  'surface-card',
  'surface-page',
  'border',
  'border-strong',
  'bg-accent',
] as const;

export type DashboardTokenName = (typeof DASHBOARD_TOKEN_NAMES)[number];

export type DashboardTokens = Record<DashboardTokenName, string>;

/**
 * Valores de respaldo, en claro.
 *
 * No son "el diseño": son el seguro para los tres casos en que `getComputedStyle`
 * no devuelve nada — SSR, un test con jsdom sin la hoja de estilos cargada, y el
 * primer render antes de que el CSS del host esté aplicado. Sin ellos, recharts
 * recibiría `''` y pintaría en negro.
 *
 * <b>Y NO son los colores que el navegador acaba pintando</b> — conviene saberlo antes de
 * escribir un test sobre ellos. Los `--dash-*` de `index.css` apuntan a la escala de Tailwind v4,
 * que es `oklch`, así que `--dash-danger` resuelve al `red-500` del sistema (`#fb2c36`) mientras
 * aquí dice `#D64545`, y `--dash-accent` resuelve al `primary-600` del kit (`#0e79fd`) y no a
 * `#2680EB`. Afirmar sobre estos respaldos valida la ruta de degradación, no el diseño.
 */
const FALLBACK: DashboardTokens = {
  'accent':         '#2680EB',
  'success':        '#1D9E75',
  'danger':         '#D64545',
  'text-primary':   '#1B1D21',
  'text-secondary': '#61646B',
  'text-muted':     '#9095A0',
  'surface-card':   '#FFFFFF',
  'surface-page':   '#F4F5F7',
  'border':         '#E4E7EB',
  'border-strong':  '#CBD0D8',
  'bg-accent':      '#EAF2FD',
};

/** Lee un `--dash-*` del elemento raíz y lo devuelve resuelto. */
function readToken(name: DashboardTokenName): string {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') {
    return FALLBACK[name];
  }
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--dash-${name}`)
    .trim();
  return value || FALLBACK[name];
}

/** Resuelve los once tokens de una sola pasada. */
export function readDashboardTokens(): DashboardTokens {
  return DASHBOARD_TOKEN_NAMES.reduce((acc, name) => {
    acc[name] = readToken(name);
    return acc;
  }, {} as DashboardTokens);
}

/**
 * Los tokens del tablero, ya resueltos a color, listos para pasarle a `recharts`.
 *
 * Se revalúan cuando cambia el tema, porque el `ThemeProvider` del kit conmuta
 * la clase `dark` sobre `<html>` y las variables cambian de valor sin que React
 * se entere: un `MutationObserver` sobre el atributo `class` de la raíz es la
 * única forma de que un color pasado por prop siga al tema.
 *
 * @example
 * const tokens = useDashboardTokens();
 * <Line stroke={tokens.accent} />
 */
export function useDashboardTokens(): DashboardTokens {
  const [tokens, setTokens] = useState<DashboardTokens>(readDashboardTokens);

  const refresh = useCallback(() => setTokens(readDashboardTokens()), []);

  useEffect(() => {
    // Primera resolución tras el montaje: en el render inicial la hoja del host
    // puede no estar aplicada todavía y habríamos devuelto los respaldos.
    refresh();

    if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return;

    const observer = new MutationObserver(refresh);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [refresh]);

  return useMemo(() => tokens, [tokens]);
}
